/**
 * A second reader for the claims check, from a different model family than the writer.
 *
 * The in-product claims review runs on the same Gemini family that writes the ad, and on the
 * ad-copy eval it missed most invented benefits ("keeps your edge steady", "stays set every
 * swing") that an independent judge caught (5 Nov 2026: unstated detail stayed near 60% after the
 * Gemini review learned the same rule). This asks Claude one narrow question, which phrases the
 * evidence does not state, and returns them for the removal step. Server-only; with no key, or on
 * any failure, it returns nothing and the ad is judged as before.
 */

import { AD_CLAIMS_CHECK_MODEL } from '@/lib/ai-agent/model-constants';

const ANTHROPIC_MESSAGES_URL = 'https://api.anthropic.com/v1/messages';
const ANTHROPIC_VERSION = '2023-06-01';
const TIMEOUT_MS = 20_000;

const SYSTEM = [
  'You check an ad against the facts it may state. Reply with JSON only: {"unsupported": ["exact phrase", ...]}.',
  'List each phrase in the ad, copied exactly, that states something the FACTS do not: a product benefit or performance result (how it feels, grips, holds, lasts or performs, what it stops or prevents, what the reader will be able to do), availability, stock, shipping, delivery, a price advantage, "free" or "no cost", timing, season, a place, a narrower or different audience, a cause, or a promised outcome.',
  'Do not list: a fact the FACTS state, a fair restatement of one, the product or brand name, a plain call to action, or a question.',
  'Copy each phrase character for character from the ad, the shortest phrase that carries the unstated claim. Return an empty list when everything is stated.',
].join('\n');

export async function findUnstatedClaimsCrossModel(input: {
  adText: string;
  facts: readonly string[];
  apiKey?: string;
  fetchImpl?: typeof fetch;
}): Promise<string[]> {
  const apiKey = input.apiKey ?? process.env.ANTHROPIC_API_KEY;
  const facts = input.facts.map((line) => line.trim()).filter(Boolean);
  if (!apiKey) {
    console.error('[ad-copy.cross-model-claims] skipped: ANTHROPIC_API_KEY is not set');
    return [];
  }
  if (facts.length === 0 || !input.adText.trim()) return [];
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await (input.fetchImpl ?? fetch)(ANTHROPIC_MESSAGES_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': ANTHROPIC_VERSION,
      },
      body: JSON.stringify({
        model: AD_CLAIMS_CHECK_MODEL,
        max_tokens: 600,
        system: SYSTEM,
        messages: [
          {
            role: 'user',
            content: `FACTS:\n${facts.map((line) => `- ${line}`).join('\n')}\n\nAD:\n${input.adText}`,
          },
        ],
      }),
      signal: controller.signal,
    });
    if (!res.ok) {
      console.error(
        '[ad-copy.cross-model-claims] request failed',
        res.status,
        (await res.text().catch(() => '')).slice(0, 300),
      );
      return [];
    }
    const body = (await res.json().catch(() => null)) as {
      content?: Array<{ type?: string; text?: string }>;
    } | null;
    const text = (body?.content ?? [])
      .filter((block) => block.type === 'text')
      .map((block) => block.text ?? '')
      .join('');
    const json = text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1);
    const parsed = JSON.parse(json) as { unsupported?: unknown };
    const phrases = Array.isArray(parsed.unsupported) ? parsed.unsupported : [];
    const found = phrases
      .filter((phrase): phrase is string => typeof phrase === 'string')
      .map((phrase) => phrase.trim())
      .filter((phrase) => phrase.length > 2 && input.adText.includes(phrase))
      .slice(0, 12);
    return found;
  } catch (error) {
    console.error('[ad-copy.cross-model-claims] failed', error);
    return [];
  } finally {
    clearTimeout(timer);
  }
}
